import { CORE_DEVICE_LOCALE, CORE_DEVICE_TIMEZONE, CORE_WEBSOCKET_BASE_URL } from '../../core.tokens';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, EMPTY, merge, of, Subject, Subscription, timer } from 'rxjs';
import {
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  share,
  skip,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { webSocket } from 'rxjs/webSocket';
import { AuthService } from './auth.service';
import { NetworkService } from './network.service';
import { WatchdogService } from './watchdog.service';
import { LoginMetadata, MessageMap, WebSocketResponse } from '../models/websocket.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ClusterService } from './cluster.service';
import { isNotNull } from '../utils';

@Injectable()
export class WebsocketService {

  private readonly websocketBaseUrl = inject(CORE_WEBSOCKET_BASE_URL);
  private readonly deviceLocale = inject(CORE_DEVICE_LOCALE);
  private readonly deviceTimezone = inject(CORE_DEVICE_TIMEZONE);
  private readonly destroyRef = inject(DestroyRef);
  private readonly auth = inject(AuthService);
  private readonly cluster = inject(ClusterService);
  private readonly watchdog = inject(WatchdogService);
  private readonly network = inject(NetworkService);

  private readonly logger = this.watchdog.tag('WebSocket', 'magenta');

  private wsSub: Subscription | null = null;
  private timeoutSub: Subscription | null = null;
  private heartbeatSub: Subscription | null = null;
  private handshakeSub: Subscription | null = null;

  private readonly statusSubject = new BehaviorSubject<boolean>(false);
  private readonly connectionCountSubject = new BehaviorSubject<number>(0);
  private readonly lastConnectionAtSubject = new BehaviorSubject<Date | null>(null);
  private readonly lastDisconnectionAtSubject = new BehaviorSubject<Date | null>(null);

  public readonly status$ = this.statusSubject.asObservable().pipe(distinctUntilChanged());
  public readonly connectionCount$ = this.connectionCountSubject.asObservable();
  public readonly lastConnectionAt$ = this.lastConnectionAtSubject.asObservable();
  public readonly lastDisconnectionAt$ = this.lastDisconnectionAtSubject.asObservable();

  private readonly ws$ = webSocket<WebSocketResponse>({
    url: `${ this.websocketBaseUrl }/ws/main`,
    openObserver: {
      next: () => {
        this.logger.info('Open');
        this.handshake();
      },
    },
    closingObserver: {
      next: () => {
        this.logger.info('Closing');
        this.markDisconnected();
        this.unsubscribeHandshake();
      },
    },
    closeObserver: {
      next: () => {
        this.logger.info('Close');
        this.markDisconnected();
        this.unsubscribeHandshake();
      },
    },
  });

  private readonly messagesSubject = new Subject<WebSocketResponse>();

  public readonly messages$ = this.messagesSubject.asObservable();

  public initialize(): void {
    this.logger.info('Initialize');

    this.status$.pipe(
      skip(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((status) => {
      if (status) {
        this.connectionCountSubject.next(this.connectionCountSubject.getValue() + 1);
        this.lastConnectionAtSubject.next(new Date());
      }
      else {
        this.lastDisconnectionAtSubject.next(new Date());
      }
    });

    combineLatest([
      this.cluster.leader$,
      this.auth.authorized$,
      this.network.status$.pipe(distinctUntilChanged()),
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(([leader, authorized, status]) => {
      if (!leader || !authorized || !status) {
        this.unsubscribeAll();
      }
      else {
        this.initWebSocket();
      }
    });
  }

  public send<K extends keyof MessageMap>(type: K, data: MessageMap[K]): void {
    this.logger.debug('Send', type, data);
    this.ws$.next({ type, data });
  }

  private handshake(): void {
    this.unsubscribeHandshake();
    this.handshakeSub = new Subscription();

    const metadata$ = this.cluster.members$.pipe(
      map((members) => {
        return {
          device: {
            locale: this.deviceLocale,
            timezone: this.deviceTimezone,
            screen: {
              width: window.screen.width,
              height: window.screen.height,
            },
          },
          app: this.cluster.appMetadata,
          sideCarApps: (
            Array.from(members.values()).filter((member) => {
              return member.app.startupId !== this.cluster.appMetadata.startupId;
            }).map((member) => {
              return member.app;
            })
          ),
        } satisfies LoginMetadata;
      }),
      share(),
    );

    this.handshakeSub.add(
      combineLatest([
        this.auth.token$.pipe(filter(isNotNull)),
        metadata$,
      ]).pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(([token, metadata]) => {
        this.send('login', {
          token,
          metadata: metadata,
        });
      }),
    );

    this.handshakeSub.add(
      metadata$.pipe(
        skip(1),
        debounceTime(300),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe((metadata) => {
        this.send('clusterChanged', metadata);
      }),
    );
  }

  private unsubscribeAll(): void {
    this.wsSub?.unsubscribe();
    this.wsSub = null;
    this.timeoutSub?.unsubscribe();
    this.timeoutSub = null;
    this.heartbeatSub?.unsubscribe();
    this.heartbeatSub = null;
    this.unsubscribeHandshake();
  }

  private unsubscribeHandshake(): void {
    this.handshakeSub?.unsubscribe();
    this.handshakeSub = null;
  }

  private initWebSocket(): void {
    this.unsubscribeAll();

    const share$ = this.ws$.pipe(
      filter((response) => !!(response?.type)),
      catchError((error) => {
        this.logger.error(error);
        this.markDisconnected();
        return EMPTY;
      }),
      share(),
    );

    this.timeoutSub = merge(of('init'), share$).pipe(
      switchMap(() => of('timeout').pipe(delay(10000))),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((msg) => {
      this.logger.error(msg);
      this.initWebSocket();
    });

    this.heartbeatSub = timer(1000, 5000).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.logger.debug('Heartbeat send', new Date().toISOString());
      this.send('heartbeat', { time: Date.now().toString() });
    });

    this.wsSub = share$.pipe(
      tap((response) => {
        this.messagesSubject.next(response);

        switch (response.type) {
          case 'loginSuccessful':
            this.logger.debug('Received: Login successful.');
            this.markConnected();
            break;

          case 'loginError':
          case 'deleteTable':
            this.logger.debug('Received: ' + response.type === 'loginError' ? 'Login error.' : 'Delete table.');
            this.auth.logout();
            this.ws$.complete();
            break;

          case 'heartbeat':
            this.logger.debug('Received: Heartbeat', response.data?.time);
            break;

          case 'brightnessSettings':
            this.logger.debug('Received: Brightness Settings', response.data);
            break;

          case 'error':
            this.logger.error('Received: Error', response.data);
            break;

          default:
            this.logger.debug('Received: Unknown message', response);
            break;
        }
      }),
      catchError((error) => {
        this.logger.error(error);

        this.initWebSocket();
        return EMPTY;
      }),
      finalize(() => this.logger.info('Complete')),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  private markConnected(): void {
    this.statusSubject.next(true);
  }

  private markDisconnected(): void {
    this.statusSubject.next(false);
  }

}
